import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Space, Row, Col, Spin, message } from 'antd';
import {
    StarFilled,
    UserOutlined,
    RobotOutlined,
    CrownOutlined,
    GiftOutlined,
    CheckCircleOutlined,
    LoadingOutlined,
    DownOutlined,
    UpOutlined
} from '@ant-design/icons';
import drEasyApi from '../../Api/Customapi';
import { useLanguage } from '../../context/LanguageContext';

const { Title, Text } = Typography;

export default function PlansSection() {
    const [realPlans, setRealPlans] = useState([]);
    const [plansLoading, setPlansLoading] = useState(false);
    const [animateCards, setAnimateCards] = useState(false);
    const [expandedPlans, setExpandedPlans] = useState({});
  const { t, isRTL } = useLanguage();
    const getUniqueFeatures = (currentPlan, allPlans) => {
        const currentLevel = currentPlan.level;
        const currentFeatures = currentPlan.features || [];

        if (currentLevel === 0) {
            return currentFeatures;
        }

        const previousPlansFeatures = allPlans
            .filter(plan => plan.level < currentLevel)
            .flatMap(plan => plan.features || [])
            .map(feature => feature.featureName || feature.name);

        const uniqueFeatures = currentFeatures.filter(feature =>
            !previousPlansFeatures.includes(feature.featureName || feature.name)
        );

        return uniqueFeatures;
    };

    const hasPreviousFeatures = (currentPlan, allPlans) => {
        return currentPlan.level > 0;
    };

    const getPlanIcon = (level) => {
        const icons = {
            0: <UserOutlined />,
            1: <RobotOutlined />,
            2: <CrownOutlined />
        };
        return icons[level] || <GiftOutlined />;
    };

    const getPlanColor = (level) => {
        const colors = {
            0: '#fbbf24',
            1: '#fbbf24',
            2: '#faad14'
        };
        return colors[level] || '#1890ff';
    };

    const getPreviousPlanName = (level) => {
        return level === 1 ? "Smart Start" : "Pro Clinic";
    };

    const togglePlanExpansion = (planId) => {
        setExpandedPlans(prev => ({
            ...prev,
            [planId]: !prev[planId]
        }));
    };

    // Fetch real plans from API
    const fetchPlans = async () => {
        setPlansLoading(true);
        try {
            const plansRes = await drEasyApi.get('/api/Plans/GetAllPlans',{headers:
{
                "Accept-Language": isRTL ? 'ar' : 'en'}
            });

            const sortedPlans = Array.isArray(plansRes.data)
                ? plansRes.data.sort((a, b) => a.level - b.level)
                : [];

            setRealPlans(sortedPlans);
        } catch (error) {
            console.error('Error fetching plans:', error);
            message.error('Failed to load plans');
            setRealPlans([]);
        }
        setPlansLoading(false);
    };

    useEffect(() => {
        fetchPlans();
        setTimeout(() => setAnimateCards(true), 200);
    }, [isRTL]);

    const handlePlanSelect = (planName) => {
        message.success(`You selected ${planName} plan!`);
    };

    return (
        <div className='md:min-h-screen'>

        <section className="py-12 sm:py-16 bg-gradient-to-r from-secondary to-primary md:max-h-[500px]">
            <div className=" mx-auto px-4 sm:px-6 lg:px-28">
                {/* Header */}
                <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                    <Title
                        level={1}
                        className="text-white mb-4 sm:mb-6"
                        style={{
                            color: "white",
                            fontSize: 'clamp(28px, 5vw, 48px)',
                            fontWeight: 'bold',
                            lineHeight: 1.2
                        }}
                    >
{t('comingSoon.plans.choosePlan') || 'Our Plans'}
                    </Title>
                    <Text
                        className="text-white/90 block mb-6 sm:mb-8"
                        style={{
                            fontSize: 'clamp(19px, 3.5vw, 28px)',
                            lineHeight: 1.6,
                            margin: '0 auto'
                        }}
                    >
{t('comingSoon.plans.freeTrialBanner') || 'Our Plans'}
                    </Text>
                </div>

                {/* Plans Grid */}
                <Spin spinning={plansLoading} indicator={<LoadingOutlined style={{ fontSize: 48, color: 'white' }} spin />}>
                    <Row gutter={[16, 24]} className="mb-8 gap-5 justify-center">
                        {realPlans.map((plan, index) => {
                            const uniqueFeatures = getUniqueFeatures(plan, realPlans);
                            const hasInheritedFeatures = hasPreviousFeatures(plan, realPlans);
                            const isExpanded = expandedPlans[plan.id];
                            const displayFeatures = isExpanded ? uniqueFeatures : uniqueFeatures.slice(0, 3);
                            const hasMoreFeatures = uniqueFeatures.length > 3;

                            return (
                                <Col
                                    xs={24}
                                    sm={24}
                                    md={8}
                                    lg={7}
                                    key={plan.id}
                                >
                                    <Card
                                        className={`h-full border-0 shadow-2xl transition-all duration-500 hover:scale-105 ${animateCards ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                                            }`}
                                        style={{
                                            transitionDelay: `${index * 100}ms`,
                                            border: plan.level === 1 ? '3px solid #fbbf24' : '1px solid #e5e7eb',
                                            transform: plan.level === 1 ? 'scale(1.05)' : 'scale(1)',
                                            background: 'white',
                                            borderRadius: '16px',
                                            position: 'relative',
                                        }}
                                    >
                                        <div className="text-center px-1">
                                            {/* Icon */}
                                            <div
                                                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 text-white"
                                                style={{
                                                    background: getPlanColor(plan.level),
                                                    fontSize: 'clamp(24px, 4vw, 32px)'
                                                }}
                                            >
                                                {getPlanIcon(plan.level)}
                                            </div>

                                            {/* Plan Name */}
                                            <Title
                                                level={2}
                                                className="text-gray-800 mb-4"
                                                style={{
                                                    fontSize: 'clamp(20px, 3vw, 28px)',
                                                    lineHeight: 1.2
                                                }}
                                            >
                                                {plan.name}
                                            </Title>

                                            {/* Pricing */}
                                            <div className="mb-6 sm:mb-8">
                                                <div className="flex items-center justify-center gap-2 mb-2 flex-wrap">
                                                    {plan.level === 2 && (
                                                        <Text
                                                            delete
                                                            className="text-gray-400"
                                                            style={{ fontSize: 'clamp(14px, 2vw, 18px)' }}
                                                        >
                                                            EGP {plan.monthlyPrice}
                                                        </Text>
                                                    )}
                                                    <Text
                                                        className="font-bold"
                                                        style={{
                                                            fontSize: plan.level === 2 ? 'clamp(28px, 4vw, 40px)' : 'clamp(20px, 3vw,30 px)',
                                                            color: plan.level === 2 ? '#52c41a' : getPlanColor(plan.level)
                                                        }}
                                                    >
                                                        {plan.level === 2 ? '30 Days' : `EGP ${plan.monthlyPrice}`}
                                                    </Text>
                                                </div>
                                                <Text className="text-gray-600 block" style={{ fontSize: 'clamp(12px, 1.5vw, 14px)' }}>
                                                    {plan.level === 2 ? 'Free Trial Period' : 'per month'}
                                                </Text>

                                            </div>

                                            {/* Features */}
                                            <div className="text-left mb-4 sm:mb-6">
                                                <Title
                                                    level={5}
                                                    className="mb-4 text-gray-800"
                                                    style={{ fontSize: 'clamp(14px, 2vw, 16px)' }}
                                                >
                                                    Features Included:
                                                </Title>

                                                <Space direction="vertical" size="small" className="w-full">
                                                    {hasInheritedFeatures && (
                                                        <div className="flex items-start gap-3">
                                                            <CheckCircleOutlined className="text-green-500 mt-1 flex-shrink-0" style={{ fontSize: 'clamp(12px, 1.1vw, 12px)' }} />
                                                            <Text className="text-gray-600" style={{ fontSize: 'clamp(12px, 1.5vw, 14px)' }}>
                                                                Everything in <b>{getPreviousPlanName(plan.level)}</b>
                                                            </Text>
                                                        </div>
                                                    )}

                                                    {displayFeatures.map((feature, fIndex) => (
                                                        <div key={fIndex} className="flex items-start gap-3">
                                                            <CheckCircleOutlined className="text-green-500 mt-1 flex-shrink-0" style={{ fontSize: 'clamp(12px, 1.5vw, 14px)' }} />
                                                            <Text className="text-gray-600" style={{ fontSize: 'clamp(12px, 1.5vw, 14px)', lineHeight: 1.4 }}>
                                                                {feature.featureName || feature.name}
                                                            </Text>
                                                        </div>
                                                    ))}
                                                </Space>

                                                {/* See More/Less Button */}
                                                {hasMoreFeatures && (
                                                    <Button
                                                        type="link"
                                                        onClick={() => togglePlanExpansion(plan.id)}
                                                        className="mt-3 p-0 h-auto"
                                                        style={{
                                                            fontSize: 'clamp(12px, 1.5vw, 14px)',
                                                            color: getPlanColor(plan.level),
                                                            fontWeight: '600'
                                                        }}
                                                    >
                                                        {isExpanded ? (
                                                            <>
                                                                Show Less <UpOutlined className="ml-1" />
                                                            </>
                                                        ) : (
                                                            <>
                                                                See More ({uniqueFeatures.length - 3} more) <DownOutlined className="ml-1" />
                                                            </>
                                                        )}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                </Spin>

                {/* Loading State */}
                {plansLoading && (
                    <div className="text-center text-white py-8" style={{ fontSize: 'clamp(14px, 2vw, 16px)' }}>
                        Loading plans...
                    </div>
                )}

                {/* Empty State */}
                {!plansLoading && realPlans.length === 0 && (
                    <div className="text-center text-white py-12">
                        <GiftOutlined style={{ fontSize: 'clamp(32px, 6vw, 48px)', marginBottom: '16px' }} />
                        <div style={{ fontSize: 'clamp(14px, 2vw, 16px)' }}>No plans available</div>
                        <div style={{ fontSize: 'clamp(12px, 1.5vw, 14px)', opacity: 0.8, marginTop: '8px' }}>
                            Please check back later
                        </div>
                    </div>
                )}

                {/* Support Note */}
                <div className="text-center mt-8 sm:mt-12">
                    <Text className="text-white/80" style={{ fontSize: 'clamp(12px, 1.5vw, 14px)', lineHeight: 1.6 }}>
                        Need help choosing? Contact our support team for personalized recommendations.
                    </Text>
                </div>
            </div>
        </section>
    </div>
    );
}